import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import LotteriesApi from '../../../Api/CRM/Modules/LotteriesApi';
import CustomControlCheckbox from '../../elements/formElements/CustomControlCheckbox';
import FormGroup from '../../elements/formElements/FormGroup';
import FormGroupDateRange from '../../elements/formElements/FormGroupDateRange';
import RadioGroup from '../../elements/formElements/RadioGroup';
import Select from '../../elements/formElements/Select';
import SubmitButton from '../../elements/formElements/SubmitButton';
import axios from '../../services/MyAxios';

const currencyCode = process.env.MIX_CURRENCY;

function PromoCodeForm({ action, promoCodeData, errors, isLoading, setIsLoading }) {
    const [affiliates, setAffiliates] = useState([]);
    const [lotteries, setLotteries] = useState([]);
    const [promoCode, setPromoCode] = useState({
        use_place: 'deposit',
        bonus: 'ticket',
        prefix: '',
        affiliate_id: '',
        lottery_id: '',
        discount_amount: 0,
        max_uses: 0,
        start_date: null,
        end_date: null,
        is_enabled: 0,
        code_type: 'single',
        code_length: 2,
        number_of_codes: 0,
    });
    const history = useHistory();
    const affiliatesUrl = '/crea/api/affiliates_short';

    useEffect(() => {
        axios.get(affiliatesUrl).then((response) => {
            setAffiliates(response.data);
        });
        LotteriesApi.getNames(false).then((response) => setLotteries(response));
    }, []);

    useEffect(() => {
        if (promoCodeData) {
            setPromoCode({
                ...promoCode,
                use_place: promoCodeData.use_place,
                prefix: promoCodeData.prefix ?? '',
                affiliate_id: promoCodeData.affiliate_id ?? '',
                lottery_id: promoCodeData.lottery_id ?? '',
                bonus: promoCodeData.bonus,
                discount_amount: promoCodeData.discount_amount,
                max_uses: promoCodeData.max_uses,
                start_date: moment(promoCodeData.start_date),
                end_date: moment(promoCodeData.end_date),
                is_enabled: promoCodeData.is_enabled,
            });
        }
    }, [promoCodeData]);

    function handleSubmit(event) {
        setIsLoading(true);
        event.preventDefault();

        if (promoCode.discount_amount === 0) {
            promoCode.discount_amount = null;
        }

        action(promoCode);
    }

    function cancel() {
        history.push('/promo_codes');
    }

    function getErrorFor(field) {
        if (errors[field]) {
            return errors[field][0];
        }
    }

    return (
        <form onSubmit={handleSubmit} autoComplete="do-not-autofill">
            <div className="card-body">
                <h6 className="card-title">Select where promo code can be used:</h6>
                <RadioGroup
                    field="use_place"
                    values={['deposit', 'purchase', 'register']}
                    labels={['Deposit', 'Purchase', 'Register']}
                    checked={promoCode.use_place}
                    error={getErrorFor('use_place')}
                    handleChange={(value) =>
                        setPromoCode({
                            ...promoCode,
                            use_place: value,
                            bonus: 'ticket',
                            discount_amount: 0,
                        })
                    }
                />
                <Select
                    field="affiliate_id"
                    value={promoCode.affiliate_id}
                    label="Affiliate"
                    options={affiliates}
                    error={getErrorFor('affiliate_id')}
                    handleChange={(event) => setPromoCode({ ...promoCode, affiliate_id: event.target.value })}
                />
            </div>
            <div className="card-body bg-light">
                <h6 className="card-title" key="code_type_label">
                    Code type:
                </h6>
                <RadioGroup
                    key="code_type"
                    field="code_type"
                    values={['single', 'series']}
                    labels={['Single', 'Series']}
                    checked={promoCode.code_type}
                    error={getErrorFor('code_type')}
                    handleChange={(value) => setPromoCode({ ...promoCode, code_type: value })}
                />
                <FormGroup
                    type="text"
                    field="prefix"
                    value={promoCode.prefix}
                    label="Prefix"
                    error={getErrorFor('prefix')}
                    handleChange={(event) => setPromoCode({ ...promoCode, prefix: event.target.value })}
                />
                {promoCode.code_type === 'series' && [
                    <FormGroup
                        key="code_length"
                        type="text"
                        field="code_length"
                        value={promoCode.code_length}
                        label="Code length"
                        error={getErrorFor('code_length')}
                        handleChange={(event) => setPromoCode({ ...promoCode, code_length: event.target.value })}
                    />,
                    <FormGroup
                        key="number_of_codes"
                        type="text"
                        field="number_of_codes"
                        value={promoCode.number_of_codes}
                        label="Number of codes"
                        error={getErrorFor('number_of_codes')}
                        handleChange={(event) =>
                            setPromoCode({
                                ...promoCode,
                                number_of_codes: event.target.value,
                            })
                        }
                    />,
                ]}
            </div>
            <div className="card-body">
                <h6 className="card-title">Bonus type:</h6>
                <RadioGroup
                    field="bonus"
                    values={['ticket']}
                    labels={['Ticket']}
                    checked={promoCode.bonus}
                    error={getErrorFor('bonus')}
                    handleChange={(value) =>
                        setPromoCode({
                            ...promoCode,
                            bonus: value,
                            discount_amount: 0,
                        })
                    }
                />
                {promoCode.use_place === 'purchase' && [
                    <h6 key="discount">Discount</h6>,
                    <RadioGroup
                        key="discount_type"
                        field="bonus"
                        values={['percentage', 'amount']}
                        labels={['%', currencyCode]}
                        checked={promoCode.bonus}
                        error={getErrorFor('bonus')}
                        handleChange={(value) => setPromoCode({ ...promoCode, bonus: `${value}_discount` })}
                    />,
                ]}
                {promoCode.bonus.includes('discount') && (
                    <FormGroup
                        key="discount_amount"
                        type="text"
                        field="discount_amount"
                        value={promoCode.discount_amount}
                        label="Discount amount"
                        error={getErrorFor('discount_amount')}
                        handleChange={(event) =>
                            setPromoCode({
                                ...promoCode,
                                discount_amount: event.target.value,
                            })
                        }
                    />
                )}
                <Select
                    field="lottery_id"
                    value={promoCode.lottery_id}
                    label="Lottery"
                    options={lotteries}
                    error={getErrorFor('lottery_id')}
                    handleChange={(event) => setPromoCode({ ...promoCode, lottery_id: event.target.value })}
                    nullCaption={promoCode.bonus === 'ticket' ? '\u2014' : 'All lotteries'}
                />
            </div>
            <div className="card-body bg-light">
                <FormGroup
                    type="text"
                    field="max_uses"
                    value={promoCode.max_uses}
                    label="Max number of uses of one code"
                    error={getErrorFor('max_uses')}
                    handleChange={(event) =>
                        setPromoCode({
                            ...promoCode,
                            max_uses: event.target.value,
                        })
                    }
                />
                <FormGroupDateRange
                    startDate={promoCode.start_date}
                    endDate={promoCode.end_date}
                    label="Validity dates"
                    errorStartDate={getErrorFor('start_date')}
                    errorEndDate={getErrorFor('end_date')}
                    handleChange={(dates) => {
                        setPromoCode({
                            ...promoCode,
                            start_date: dates[0],
                            end_date: dates[1],
                        });
                    }}
                />
                <CustomControlCheckbox
                    field="is_enabled"
                    value={promoCode.is_enabled === 1}
                    label="Enabled"
                    error={getErrorFor('is_enabled')}
                    handleChange={(event) => setPromoCode({ ...promoCode, is_enabled: event.target.checked ? 1 : 0 })}
                />
            </div>
            <div className="card-body">
                <button type="button" onClick={cancel} className="btn btn-secondary">
                    Cancel
                </button>
                <SubmitButton label="Save" isLoading={isLoading} />
            </div>
        </form>
    );
}

export default PromoCodeForm;
